import React, { useEffect, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import UcleusVideo from './ucleusVideo';

// Custom article styles live inside a scriptless document, never in the app shell.
export default function UcleusArticle({ html, title }) {
  const frameRef = useRef(null);
  const cleanupRef = useRef(() => {});
  const documentHtml = useMemo(() => {
    const content = DOMPurify.sanitize(html || '', {
      USE_PROFILES: { html: true },
      ADD_TAGS: ['style'],
      ADD_ATTR: ['style', 'controls', 'playsinline', 'poster'],
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'textarea', 'select', 'link', 'meta', 'base'],
      FORBID_ATTR: ['contenteditable', 'autofocus', 'controls'],
    });
    // Replace only generated defaults; keep the author's custom CSS intact.
    const legacyAccent = ['#f9', '7316'].join('');
    const legacyLink = ['#c2', '410c'].join('');
    const parsed = new DOMParser().parseFromString(content, 'text/html');
    parsed.querySelectorAll('blockquote').forEach(quote => {
      if (!quote.textContent.trim() && !quote.querySelector('img,video,audio,svg,table')) quote.remove();
    });
    parsed.querySelectorAll('style[data-ucleus-base]').forEach(style => {
      style.textContent = style.textContent
        .replace('padding:20px', 'padding:0')
        .replaceAll('#4b5563', 'var(--ucleus-muted,#4b5563)')
        .replaceAll('#d1d5db', 'var(--ucleus-border,#d1d5db)')
        .replaceAll('#f3f4f6', 'var(--ucleus-surface,#f3f4f6)')
        .replaceAll('#fff7ed', 'var(--ucleus-surface,#fff7ed)')
        .replaceAll(legacyAccent, 'var(--ucleus-accent,#f7d135)')
        .replaceAll(legacyLink, 'var(--ucleus-link,#7a5c00)');
    });
    return `<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"><meta http-equiv="Content-Security-Policy" content="script-src 'none'; object-src 'none'; frame-src 'none'; base-uri 'none'; form-action 'none'"><style>html,body{margin:0;padding:0}body{overflow-wrap:anywhere}img,video{max-width:100%;height:auto}video{display:block;width:100%}table{max-width:100%}a{overflow-wrap:anywhere}</style></head><body>${parsed.body.innerHTML}</body></html>`;
  }, [html]);

  const connect = () => {
    cleanupRef.current();
    const frame = frameRef.current;
    const doc = frame?.contentDocument;
    if (!doc?.body) return;
    const disposePlayers = UcleusVideo.enhance(doc);
    const updateTheme = () => {
      const styles = getComputedStyle(frame.parentElement);
      doc.documentElement.style.setProperty('--ucleus-text', styles.color);
      doc.documentElement.style.setProperty('--ucleus-bg', 'transparent');
      const tokens = getComputedStyle(document.documentElement);
      Object.entries({ muted: 'text-muted', border: 'border', surface: 'surface-alt', accent: 'accent', link: 'link' }).forEach(([name, token]) => {
        doc.documentElement.style.setProperty(`--ucleus-${name}`, tokens.getPropertyValue(`--color-${token}`));
      });
      doc.documentElement.style.colorScheme = document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
    };
    const resize = () => {
      const height = Math.max(80, Math.ceil(doc.body.getBoundingClientRect().height + 8));
      frame.style.height = `${Math.min(height, 100000)}px`;
    };
    const observer = new ResizeObserver(resize);
    observer.observe(doc.body);
    const themeObserver = new MutationObserver(updateTheme);
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme', 'style'] });
    themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-theme', 'style'] });
    // Normal article links navigate outside the frame, after protocol validation.
    const openLink = event => {
      const link = event.target.closest('a[href]');
      if (!link) return;
      event.preventDefault();
      try {
        const url = new URL(link.getAttribute('href'), window.location.href);
        if (['https:', 'http:', 'mailto:'].includes(url.protocol)) window.location.assign(url.href);
      } catch { /* An invalid authored link remains inert. */ }
    };
    doc.addEventListener('click', openLink);
    updateTheme();
    resize();
    cleanupRef.current = () => { disposePlayers(); observer.disconnect(); themeObserver.disconnect(); doc.removeEventListener('click', openLink); };
  };

  useEffect(() => () => cleanupRef.current(), []);

  return <iframe ref={frameRef} title={`${title || 'Article'} — content`} srcDoc={documentHtml}
    sandbox="allow-same-origin" allowFullScreen referrerPolicy="no-referrer" onLoad={connect}
    style={{ display: 'block', width: '100%', minHeight: 80, border: 0, background: 'transparent' }} />;
}
